import {
    Component,
    effect,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import {
    IconResolver,
    MatIconModule,
    MatIconRegistry,
} from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer } from '@angular/platform-browser';
import { filter } from 'rxjs';
import 'ag-grid-enterprise';

import { AuthService } from '@services/api/auth/auth.service';
import { PostLoginService } from '@services/post-login.service';
import { UserDataService } from '@services/data/user-data.service';
import { LoaderComponent } from '@shared/loader/loader.component';
import { ScriptLoaderService } from '@services/script-loader-service';
import { NewRelicService } from '@services/newrelic.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { NavbarComponent, NavItem } from '@shared/navbar/navbar.component';
import { BrandingBarComponent } from '@shared/branding-bar/branding-bar.component';
import { TenantSelectionComponent } from '@features/tenant-selection/tenant-selection.component';
import { StopClickDirective } from '@directives/stop-click.directive';
import { RoleRoutePermissions, Route } from '@constants/route';
import { NavbarTabsData } from '@constants/navbar';
import { Role, RoleLabel } from '@models/user';
import { isEmailOnlyForAllStations } from '@models/user/user-station';
import { ICONS } from '@constants/icon';
import { environment } from 'environments/environment';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-root',
    imports: [
        MatIconModule,
        MatMenuModule,
        MatButtonModule,
        RouterOutlet,
        BrandingBarComponent,
        LoaderComponent,
        NavbarComponent,
        NgTemplateOutlet,
        TenantSelectionComponent,
        StopClickDirective,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('wfMenu') wfMenu!: TemplateRef<any>;
    protected readonly dataQa = DATA_QA;
    loading = false;
    isLoggedIn = false;
    navbarVisible = false;
    navItems: NavItem[] = [];
    activeNavItemId: number | undefined;
    isErrorPage = false;
    user: {
        name: string;
        role: string;
        roleEnum: Role | null;
        rmsId: number | null;
        isEmailOnlyStation: boolean;
    } = {
        name: '',
        role: '',
        roleEnum: null,
        rmsId: null,
        isEmailOnlyStation: false,
    };

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly authService = inject(AuthService);
    private readonly iconRegistry = inject(MatIconRegistry);
    private readonly postLoginService = inject(PostLoginService);
    private readonly userDataService = inject(UserDataService);
    private readonly scriptLoader = inject(ScriptLoaderService);
    private readonly newRelicService = inject(NewRelicService);
    private readonly featureFlagService = inject(FeatureFlagService);

    constructor() {
        this.isLoggedIn = this.authService.isLoggedIn();
        this.setIconResolver();

        effect(() => {
            // Recompute tabs when feature flags update after initial login load.
            this.featureFlagService.features();

            if (this.user.roleEnum !== null) {
                this.setNavbar();
            }
        });
    }

    async ngOnInit() {
        this.listenRouteChanges();
        this.isErrorPage = /(^|\/)error(\/|$)/.test(
            this.router.url.replace(/^\//, ''),
        );
        this.setNavbarVisibility();

        if (!this.isLoggedIn) {
            return;
        }

        console.log('Initializing post-login services...');
        this.postLoginService.init();

        console.log('Waiting for user data...');
        await this.setUser();
        console.log('User data loaded:', this.user);

        const monitorPath = 'assets/monitor.js';
        console.log('Loading monitor.js...');
        await this.scriptLoader.loadScript(monitorPath);
        console.log('monitor.js loaded.');

        this.newRelicService.setUserId(this.user.rmsId?.toString() || null);
    }

    ngOnDestroy() {
        this.postLoginService.destroy();
        this.newRelicService.setUserId(null);
    }

    logout() {
        this.authService.logout();
    }

    private listenRouteChanges() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.setNavbarVisibility();
                const activeRoute = event.urlAfterRedirects;
                this.isErrorPage = /(^|\/)error(\/|$)/.test(
                    activeRoute.replace(/^\//, ''),
                );
                this.activeNavItemId = this.navItems.find(
                    (item) => item.route === activeRoute,
                )?.id;
            });
    }

    private setNavbarVisibility() {
        let currentRoute = this.route;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }
        const data = currentRoute.snapshot.data;
        this.navbarVisible = data['navbarVisible'];
    }

    private async setUser(): Promise<void> {
        return new Promise((resolve) => {
            this.userDataService.user$.subscribe({
                next: (user) => {
                    if (user) {
                        this.user = {
                            name: user.fullName ?? '',
                            role:
                                user.role !== null ? RoleLabel[user.role] : '-',
                            roleEnum: user.role,
                            rmsId: user.rmsId,
                            isEmailOnlyStation: isEmailOnlyForAllStations(
                                user.stations,
                            ),
                        };
                        this.setNavbar();
                        resolve();
                    }
                },
            });
        });
    }

    private setIconResolver() {
        const resolver: IconResolver = (name) => {
            if (ICONS.includes(name)) {
                return this.sanitizer.bypassSecurityTrustResourceUrl(
                    `/assets/icons/${name}.svg`,
                );
            }

            return null;
        };

        this.iconRegistry.addSvgIconResolver(resolver);
    }

    private setNavbar() {
        if (this.user.roleEnum === null) {
            this.navItems = [];
            return;
        }

        if (this.user.roleEnum === Role.Fixer && this.user.isEmailOnlyStation) {
            this.navItems = [];
            return;
        }

        const accountAdminGrantedAccessToConfigurations =
            this.featureFlagService.getFeature(
                'AccountAdminGrantedAccessToConfigurations',
            );
        const currentRole = RoleRoutePermissions[this.user.roleEnum];
        let permittedRoutes: Route[] = currentRole.permittedRoutes;

        if (
            this.user.roleEnum === Role.Admin &&
            !accountAdminGrantedAccessToConfigurations
        ) {
            permittedRoutes = permittedRoutes.filter(
                (route) => route !== Route.Configuration,
            );
        }

        this.navItems = NavbarTabsData.filter((tab) =>
            permittedRoutes.includes(tab.id),
        );
    }
}
